import React, { useState, useEffect } from "react";
import {
  getBilledLiftedZones,
  getBilledLiftedRegions,
  getBilledLiftedFleetsHubs,
  getBilledLiftedZonesTyreData,
  getBilledLiftedRegionsTyreData,
  getBilledLiftedFleetHubsTyreData,
  downloadBilledLiftedZonesTyres,
  downloadBilledLiftedRegionsTyres,
  downloadBilledLiftedHubsTyres,
} from "../../apis/scrappedCasingTyres.service";
import { CustomHorizontalBarChart } from "../../common/CustomHorizontalBarChart";
import Spinner from "../Spinner";
import CustomDetailModal from "./CustomDetailModal";
import { BiCloudDownload } from "react-icons/bi";
import toast from "react-hot-toast";
import { exportScrapCasingTyresExcel } from "../../utils/exportExcel";
import dayjs from "dayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import {
  BILLED_LIFTED_DETAIL_MODAL_COLUMNS,
  BILLED_LIFTED_STACK_COLORS,
} from "../../utils/scrapConstants";

const styles = {
  container: "w-full max-w-[1400px]",
  loadingWrapper:
    "w-full flex justify-center items-center h-[680px] bg-white rounded-3xl border border-[#E9DFFA] shadow-[0_4px_20px_rgba(0,0,0,0.05)] p-6",
  filterWrapper: "flex items-center max-w-[400px] gap-3 pr-1",
  dateContainer: "flex items-center gap-2 bg-white border border-[#858585] rounded-[6px] px-2 py-1 hover:border-gray-400 transition-all h-[36px]",
  dateInputGroup: "flex items-center gap-1",
  dateLabel: "text-[#2C2C2C] opacity-40 text-xs",
  input: "outline-none border-none bg-transparent text-xs font-medium text-[#2C2C2C] cursor-pointer",
};

const BilledLiftedTyresGraph = () => {
  const [viewModeBilled, setViewModeBilled] = useState("zone");
  const [zones, setZones] = useState([]);
  const [regions, setRegions] = useState([]);
  const [fleets, setFleets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedDetail, setSelectedDetail] = useState(null);
  const [modalData, setModalData] = useState([]);
  const [modalLoading, setModalLoading] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);

  const defaultDateFrom = new Date();
  defaultDateFrom.setMonth(defaultDateFrom.getMonth() - 3);
  const initialDateFrom = defaultDateFrom.toISOString().split("T")[0];
  const initialDateTo = new Date().toISOString().split("T")[0];
  const todayDate = dayjs();
  const [dateFromInput, setDateFromInput] = useState(initialDateFrom);
  const [dateToInput, setDateToInput] = useState(initialDateTo);
  const [appliedDateFrom, setAppliedDateFrom] = useState(initialDateFrom);
  const [appliedDateTo, setAppliedDateTo] = useState(initialDateTo);
  const [dateFromPickerKey, setDateFromPickerKey] = useState(0);
  const [dateToPickerKey, setDateToPickerKey] = useState(0);

  const handleDateFromChange = (value) => {
    if (!value) {
      setDateFromInput("");
      setAppliedDateFrom("");
      return;
    }
    if (!value.isValid()) {
      setDateFromPickerKey((k) => k + 1);
      return;
    }
    const nextValue = value.format("YYYY-MM-DD");
    if (value.isAfter(todayDate, "day")) {
      toast.error("From date cannot be in the future");
      setDateFromPickerKey((k) => k + 1);
      return;
    }
    if (dateToInput && value.isAfter(dayjs(dateToInput), "day")) {
      toast.error("From date cannot be after To date");
      setDateFromPickerKey((k) => k + 1);
      return;
    }
    setDateFromInput(nextValue);
    if (nextValue !== appliedDateFrom) {
      setAppliedDateFrom(nextValue);
    }
  };

  const handleDateToChange = (value) => {
    if (!value) {
      setDateToInput("");
      setAppliedDateTo("");
      return;
    }
    if (!value.isValid()) {
      setDateToPickerKey((k) => k + 1);
      return;
    }
    const nextValue = value.format("YYYY-MM-DD");
    if (value.isAfter(todayDate, "day")) {
      toast.error("To date cannot be in the future");
      setDateToPickerKey((k) => k + 1);
      return;
    }
    if (dateFromInput && value.isBefore(dayjs(dateFromInput), "day")) {
      toast.error("To date cannot be before From date");
      setDateToPickerKey((k) => k + 1);
      return;
    }
    setDateToInput(nextValue);
    if (nextValue !== appliedDateTo) {
      setAppliedDateTo(nextValue);
    }
  };

  useEffect(() => {
    fetchZones();
    setViewModeBilled("zone");
    setRegions([]);
    setFleets([]);
  }, [appliedDateFrom, appliedDateTo]);

  const fetchZones = async () => {
    setLoading(true);
    try {
      const response = await getBilledLiftedZones({ dateFrom: appliedDateFrom, dateTo: appliedDateTo });
      const zoneData = response.data || response;
      setZones(Array.isArray(zoneData) ? zoneData : []);
    } catch (error) {
      console.error("Error fetching billed zones:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleZoneClick = async (zoneName) => {
    setLoading(true);
    try {
      const response = await getBilledLiftedRegions(zoneName, { dateFrom: appliedDateFrom, dateTo: appliedDateTo });
      const regionData = response.data || response;
      setRegions(Array.isArray(regionData) ? regionData : []);
      setViewModeBilled("region");
    } catch (error) {
      console.error("Error fetching billed regions:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleRegionClick = async (regionId) => {
    setLoading(true);
    try {
      const response = await getBilledLiftedFleetsHubs(regionId, { dateFrom: appliedDateFrom, dateTo: appliedDateTo });
      const fleetData = response.data || response;
      if (Array.isArray(fleetData) && fleetData.length > 0 && fleetData[0].hubs) {
        setFleets(fleetData[0].hubs);
      } else {
        setFleets(Array.isArray(fleetData) ? fleetData : []);
      }
      setViewModeBilled("fleet");
    } catch (error) {
      console.error("Error fetching billed fleets:", error);
    } finally {
      setLoading(false);
    }
  };

  const getBilledChartData = () => {
    let rawDataList = [], activeCrumb, nextMode, labelKey, yAxisTitle;

    if (viewModeBilled === "zone") {
      rawDataList = zones;
      yAxisTitle = "Zones";
      activeCrumb = 0;
      nextMode = "region";
      labelKey = "zoneName";
    } else if (viewModeBilled === "region") {
      rawDataList = regions;
      yAxisTitle = "Regions";
      activeCrumb = 1;
      nextMode = "fleet";
      labelKey = "regionName";
    } else {
      const getTotalCount = (item) => {
        const srp = item.srpCount || item.totalScrap || item.scrapCount || 0;
        const cs1 = item.cs1Count || item.totalCasing || item.casingCount || 0;
        return item.total || (srp + cs1);
      };
      // Keep items with values toward bottom when initial scroll starts from bottom.
      rawDataList = [...fleets].sort((a, b) => getTotalCount(a) - getTotalCount(b));
      yAxisTitle = "Fleets & Hubs";
      activeCrumb = 2;
      nextMode = null;
      labelKey = "hubName";
    }

    const breadcrumbs = [
      {
        label: "Zone Wise",
        onClick: activeCrumb > 0 ? () => { setViewModeBilled("zone"); } : undefined,
        active: activeCrumb === 0,
      },
      ...(activeCrumb >= 1
        ? [
          {
            label: "Region Wise",
            onClick:
              activeCrumb > 1 ? () => { setViewModeBilled("region"); } : undefined,
            active: activeCrumb === 1,
          },
        ]
        : []),
      ...(activeCrumb >= 2
        ? [{ label: "Fleet & Hub Wise", active: true }]
        : []),
    ];

    const chartRawData = rawDataList;

    const labels = chartRawData.map((d) => {
      const name = d[labelKey] || d.fleetName || d.hubName || d.zoneName || d.regionName || "Unknown";
      // Two lines at hub level: name on top, hub code below it.
      if (viewModeBilled === "fleet" && d.hubCode) {
        return [name, `(${d.hubCode})`];
      }
      return name;
    });

    const datasets = [
      {
        label: "SRP",
        data: chartRawData.map((d) => {
          const srp = d.srpCount || d.totalScrap || d.scrapCount || 0;
          const cs1 = d.cs1Count || d.totalCasing || d.casingCount || 0;
          const total = d.total || (srp + cs1);
          return total > 0 ? (srp / total) * 100 : 0;
        }),
        counts: chartRawData.map(d => d.srpCount || d.totalScrap || d.scrapCount || 0),
        backgroundColor: BILLED_LIFTED_STACK_COLORS.srp,
      },
      {
        label: "CS1",
        data: chartRawData.map((d) => {
          const srp = d.srpCount || d.totalScrap || d.scrapCount || 0;
          const cs1 = d.cs1Count || d.totalCasing || d.casingCount || 0;
          const total = d.total || (srp + cs1);
          return total > 0 ? (cs1 / total) * 100 : 0;
        }),
        counts: chartRawData.map(d => d.cs1Count || d.totalCasing || d.casingCount || 0),
        backgroundColor: BILLED_LIFTED_STACK_COLORS.cs1,
      },
    ];

    return {
      labels,
      datasets,
      legendItems: [
        { label: "CS1", color: BILLED_LIFTED_STACK_COLORS.cs1 },
        { label: "SRP", color: BILLED_LIFTED_STACK_COLORS.srp },
      ],
      yAxisTitle,
      breadcrumbs,
      onCategoryClick: nextMode
        ? async (event, elements) => {
          if (elements && elements.length > 0) {
            const localIndex = elements[0].index;
            if (viewModeBilled === "zone") {
              const zoneName = chartRawData[localIndex].zoneName;
              await handleZoneClick(zoneName);
            } else if (viewModeBilled === "region") {
              const regionId = chartRawData[localIndex].regionCode || chartRawData[localIndex].regionName;
              await handleRegionClick(regionId);
            }
          }
        }
        : null,
      onBarClick: (event, elements) => {
        if (!elements || elements.length === 0) return;
        const localIndex = elements[0].index;
        const datasetIndex = elements[0].datasetIndex;
        const clickedRow = chartRawData[localIndex] || {};

        const labelPrefix = viewModeBilled === "zone" ? clickedRow.zoneName
          : viewModeBilled === "region" ? (clickedRow.regionName || clickedRow.regionCode)
            : `${clickedRow.hubName || clickedRow.fleetName}${clickedRow.hubCode ? ` (${clickedRow.hubCode})` : ""}`;

        const datasetLabel = datasets[datasetIndex].label;
        const value = datasets[datasetIndex].counts[localIndex];
        const tyreStatus = datasetLabel === "SRP" ? "SCRAP" : "CASING";

        setSelectedDetail({
          title: `${labelPrefix} - ${datasetLabel}`,
          resource: labelPrefix,
          type: datasetLabel,
          value,
          tyreStatus,
          viewMode: viewModeBilled,
          zoneName: clickedRow.zoneName,
          regionCode: clickedRow.regionCode || clickedRow.regionName,
          hubId: clickedRow.hubId || clickedRow._id
        });
      },
      hoverCursor: true,
    };
  };

  const billedConfig = getBilledChartData();

  useEffect(() => {
    if (!selectedDetail) {
      setModalData([]);
      return;
    }

    const fetchModalData = async () => {
      setModalLoading(true);
      try {
        let response;
        if (selectedDetail.viewMode === "zone") {
          response = await getBilledLiftedZonesTyreData({
            status: selectedDetail.tyreStatus,
            zoneName: selectedDetail.zoneName || selectedDetail.resource,
            dateFrom: appliedDateFrom,
            dateTo: appliedDateTo,
          });
        } else if (selectedDetail.viewMode === "region") {
          response = await getBilledLiftedRegionsTyreData({
            status: selectedDetail.tyreStatus,
            regionCode: selectedDetail.regionCode,
            dateFrom: appliedDateFrom,
            dateTo: appliedDateTo,
          });
        } else {
          response = await getBilledLiftedFleetHubsTyreData({
            status: selectedDetail.tyreStatus,
            hubId: selectedDetail.hubId,
            dateFrom: appliedDateFrom,
            dateTo: appliedDateTo,
          });
        }

        const rows = response?.data || response || [];
        setModalData(Array.isArray(rows) ? rows : []);
      } catch (error) {
        console.error("Error fetching billed lifted tyre modal data:", error);
        setModalData([]);
      } finally {
        setModalLoading(false);
      }
    };

    fetchModalData();
  }, [selectedDetail, appliedDateFrom, appliedDateTo]);

  const DateFilterAction = (
    <div className="flex items-center justify-end mt-2 w-auto">
      <div className={styles.filterWrapper}>
        <div
          className={styles.dateContainer}
          style={{ maxWidth: 380 }}
        >
          <div className={styles.dateInputGroup}>
            <span className={styles.dateLabel}>From:</span>
            <DatePicker
              key={`from-${dateFromPickerKey}`}
              value={dateFromInput ? dayjs(dateFromInput) : null}
              onChange={handleDateFromChange}
              format="DD/MM/YYYY"
              maxDate={dateToInput ? dayjs(dateToInput) : todayDate}
              disableFuture
              slotProps={{
                actionBar: { actions: ["clear", "today"] },
                textField: {
                  variant: "standard",
                  sx: {
                    width: 100,
                    "& input": { fontSize: 12, padding: "9px 0" },
                  },
                  InputProps: { disableUnderline: true },
                },
              }}
            />
          </div>
          <div className={styles.dateInputGroup}>
            <span className={styles.dateLabel}>To:</span>
            <DatePicker
              key={`to-${dateToPickerKey}`}
              value={dateToInput ? dayjs(dateToInput) : null}
              onChange={handleDateToChange}
              format="DD/MM/YYYY"
              minDate={dateFromInput ? dayjs(dateFromInput) : undefined}
              maxDate={todayDate}
              disableFuture
              slotProps={{
                actionBar: { actions: ["clear", "today"] },
                textField: {
                  variant: "standard",
                  sx: {
                    width: 100,
                    "& input": { fontSize: 12, padding: "9px 0" },
                  },
                  InputProps: { disableUnderline: true },
                },
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );

  const resolveDownloadTyreRows = (response) => {
    if (!response) return [];
    if (Array.isArray(response)) return response;
    if (Array.isArray(response.data)) return response.data;
    return [];
  };

  const getBilledLiftedDownloadTyreStatus = (detail) => {
    if (!detail) return "Scrap";
    const raw = detail.tyreStatus ?? detail.type;
    if (typeof raw === "string" && raw.toLowerCase() === "scrap") return "Scrap";
    if (typeof raw === "string" && raw.toLowerCase() === "casing") return "Casing";
    if (raw === "SRP") return "Scrap";
    if (raw === "CS1") return "Casing";
    return "Scrap";
  };

  const handleDownloadTyresData = async () => {
    if (!selectedDetail) return;
    setDownloadLoading(true);
    try {
      const tyreStatus = getBilledLiftedDownloadTyreStatus(selectedDetail);
      let response;
      if (selectedDetail.viewMode === "zone") {
        response = await downloadBilledLiftedZonesTyres({
          zoneName: selectedDetail.zoneName || selectedDetail.resource,
          tyreStatus,
          dateFrom: appliedDateFrom,
          dateTo: appliedDateTo,
        });
      } else if (selectedDetail.viewMode === "region") {
        response = await downloadBilledLiftedRegionsTyres({
          regionCode: String(selectedDetail.regionCode ?? ""),
          tyreStatus,
          dateFrom: appliedDateFrom,
          dateTo: appliedDateTo,
        });
      } else {
        response = await downloadBilledLiftedHubsTyres({
          hubId: selectedDetail.hubId,
          tyreStatus,
          dateFrom: appliedDateFrom,
          dateTo: appliedDateTo,
        });
      }
      const list = resolveDownloadTyreRows(response);
      if (!list.length) {
        toast.error("No data to download");
        return;
      }
      const safeTitle = `${selectedDetail.resource}-${selectedDetail.type}`.replace(/\s+/g, "_");
      exportScrapCasingTyresExcel(list, `billed-lifted-tyres_${safeTitle}`, { tyreStatus, useTAT: true });
      toast.success("File downloaded");
    } catch (error) {
      console.error("Download billed lifted tyres failed:", error);
    } finally {
      setDownloadLoading(false);
    }
  };

  return (
    <div className={styles.container}>
      {loading ? (
        <div className={styles.loadingWrapper}><Spinner /></div>
      ) : (
        <>
          <CustomHorizontalBarChart
            title="Billed & Lifted Tyres"
            titleTextSize="19px"
            boxBorder="1px solid #E9DFFA"
            cardMinHeight="680px"
            paddingLeft="20px"
            paddingTop="30px"
            paddingRight="30px"
            breadcrumbs={billedConfig.breadcrumbs}
            labels={billedConfig.labels}
            datasets={billedConfig.datasets}
            legendItems={billedConfig.legendItems}
            legendPlacement="titleRight"
            xAxisTitle="tyres"
            yAxisTitle={billedConfig.yAxisTitle}
            yAxisTitlePosition="top-[29px]"
            fixedXAxis={true}
            fixedXAxisTicks={[0, 20, 40, 60, 80, 100]}
            onCategoryClick={billedConfig.onCategoryClick}
            onBarClick={billedConfig.onBarClick}
            hoverCursor={billedConfig.hoverCursor}
            stacked={true}
            xAxisMax={100}
            xAxisStepSize={50}
            xAxisFormatter={(value) => `${value}%`}
            chartHeight="440px"
            topRightAction={DateFilterAction}
            topRightActionPlacement="breadcrumbs"
            initialVerticalScrollPosition={viewModeBilled === "fleet" ? "bottom" : "top"}
            showPercentInTooltip={true}
            showCountInTooltip={true}
            tooltipDecimalPlaces={2}
            datalabelsFormatter={(value, context) => {
              const ds = context.dataset;
              const count = ds.counts[context.dataIndex];
              if (value < 5) return "";
              return `${count}`;
            }}
            datalabelAnchor="center"
            datalabelAlign="center"
            datalabelColor={(ctx) =>
              ctx.datasetIndex === 0 ? "#374151" : "#FFFFFF"
            }
            datalabelHoverColor={(ctx) =>
              ctx.datasetIndex === 0 ? "#111827" : "#FFFFFF"
            }
            barThickness={40}
          />
        </>
      )}

      <CustomDetailModal
        isOpen={!!selectedDetail}
        onClose={() => setSelectedDetail(null)}
        title={selectedDetail?.title}
        modalWidth="85vw"
        filterComponent={
          <button
            type="button"
            className="flex items-center gap-2 bg-[#22C55E] text-white px-5 py-2.5 rounded-xl hover:bg-green-600 transition-all shadow-sm hover:shadow-md font-semibold text-sm disabled:opacity-60 disabled:cursor-not-allowed"
            disabled={downloadLoading || modalLoading || !selectedDetail}
            onClick={handleDownloadTyresData}
          >
            <BiCloudDownload size={20} className={downloadLoading ? "animate-pulse" : ""} />
            <span>{downloadLoading ? "Preparing…" : "Download Data"}</span>
          </button>
        }
        columns={BILLED_LIFTED_DETAIL_MODAL_COLUMNS}
        loading={modalLoading}
        data={modalData.map((d, idx) => ({
          id: idx,
          zoneName: d.zoneName || "-",
          regionName: d.regionName || "-",
          hubName: d.hubName || "-",
          stencilNo: d.stencilNo || "-",
          lastInspectionDate: d.lastInspectionDate
            ? new Date(d.lastInspectionDate).toLocaleDateString("en-GB")
            : "-",
          soldOutDate: d.soldOutDate
            ? new Date(d.soldOutDate).toLocaleDateString("en-GB")
            : "-",
          productType: d.productType || "-",
        }))}
      />
    </div>
  );
};

export default BilledLiftedTyresGraph;
